import { z } from 'zod'

/**
 * The single place `import.meta.env` is read. An ESLint rule bans it everywhere
 * else, so every environment value in the app is one that has been parsed and
 * validated here.
 *
 * This throws at module load. That is deliberate: a misconfigured API base URL
 * should fail loudly on the first paint, not silently produce 404s three screens
 * into a citizen's application.
 */
const schema = z.object({
  /**
   * Request timeout for every call. Named for the DHA gateway it was introduced
   * for; that gateway is no longer called directly, but the budget still matters —
   * face verification uploads a 4MB base64 image through VFS's backend.
   */
  VITE_API_TIMEOUT_MS: z.coerce.number().int().positive().default(120_000),
  VITE_APP_ENV: z.enum(['local', 'dev', 'qa', 'uat', 'prod']).default('local'),

  /** Serve the app against the MSW mock backend instead of a real upstream. */
  VITE_ENABLE_MSW: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),

  /**
   * Whether the applicant's ID number and NPR-sourced personal details may be
   * held in sessionStorage to survive a page refresh. Defaults on because the DHA
   * API offers no endpoint to re-read them, but exposed as a flag so a POPIA
   * review can switch it off without a code change.
   */
  VITE_PERSIST_APPLICANT_DETAILS: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),

  /** Spec §4.3: "Max 3 attempts (configurable)". */
  VITE_FACE_MAX_ATTEMPTS: z.coerce.number().int().positive().default(3),

  /**
   * TEMPORARY — send a fixed, enrolled face to §4.3 instead of the live capture.
   *
   * DHA has provisioned no facial biometric records for the test identities, so the
   * gateway rejects whatever the developer's camera produces — not because the match
   * failed, but because there is nothing on file to match against. With this on, the
   * capture still happens and the citizen still sees their own photo; only the base64
   * that goes on the wire is replaced, by src/assets/stub-face.jpg (see
   * src/lib/image/stubFaceImage.ts).
   *
   * It fakes the IMAGE and nothing else. §4.3's answer is honoured exactly as it
   * comes back: a returned verified:false still costs an attempt and still holds the
   * citizen on the camera board, so the endpoint is exercised for real.
   *
   * Defaults ON — a fresh clone pointed at the dev gateway should reach the later
   * steps without first editing a file. Deleted the moment DHA enrols the test
   * identities. See docs/api-gaps.md #26.
   *
   * Two guards stand behind it: vite.config.ts fails a production BUILD, and the
   * check below fails at first paint if such an artefact is ever served anyway.
   */
  VITE_FACE_MOCK_IMAGE: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),

  /** Minutes of inactivity before an in-progress application is cleared. */
  VITE_SESSION_TTL_MINUTES: z.coerce.number().int().positive().default(30),

  /**
   * VFS's backend — the app's ONLY upstream. It proxies DHA's whole application
   * lifecycle (guide §4.8), holds the DHA token itself, and logs every one of those
   * calls server-side. See docs/api-integration.md.
   *
   * The backend's https ORIGIN (UAT: `https://bolt21apiuat.vfsglobal.com`), called
   * directly from the browser, or EMPTY to call `/api/…` on the SPA's own origin through
   * an edge `/api/*` behaviour (docs/deployment-s3.md §5).
   *
   * ⚠ An origin is only valid when the SPA is served from the SAME SITE — the same
   * registrable domain, e.g. both on `*.vfsglobal.com` (ADR 0017). The backend
   * authenticates the browser with a `SameSite=Strict` session cookie (guide §2, ADR
   * 0016): same-site-but-cross-origin receives it, cross-site (an SPA on
   * `*.cloudfront.net`) never does, and every call 401-loops into `session_expired`.
   * Cross-origin also means the backend's CORS policy must name the SPA's origin with
   * credentials allowed, and answer the `X-Correlation-Id` preflight before its guards.
   *
   * `vite build` refuses a non-https value (the cookie is `Secure`). Local development
   * against a real backend goes through the Vite proxy (`DEV_API_PROXY` in
   * .env.example): localhost is cross-site to every VFS host.
   *
   * There is no credential here any more. The `clientId`/`clientSecret` pair that used
   * to sit beside this URL was inlined into the bundle by Vite, so anyone with the
   * page open could mint the token it protected — the finding this cookie model
   * closes (docs/vapt-remediation-dev-plan.md). The guard below refuses any
   * secret-shaped `VITE_*` key so it cannot come back under another name.
   */
  VITE_VFS_BACKEND_URL: z.string().default(''),

  /**
   * The route namespace every VFS backend call sits under, kept separate from the
   * host above so a deployment can move a tenant without touching code.
   *
   * DEV and PROD serve the routes bare — `/api/v1/session`, `/api/v1/application/…` —
   * and inherit the default. UAT (bolt21apiuat.vfsglobal.com) namespaces them per
   * tenant, so the same session endpoint is `/api/v1/DHASID/session` and this reads
   * `/api/v1/DHASID`.
   *
   * ⚠ The host and the namespace must not be combined into VITE_VFS_BACKEND_URL. The
   * transport concatenates base + path (buildVfsClientUrl in lib/http/vfsclient/vfsClient.ts),
   * so a base carrying the namespace would send it twice and 404 every route —
   * starting with the session bootstrap, so the whole journey stops. The check below
   * refuses that.
   */
  VITE_VFS_API_PREFIX: z.string().default('/api/v1'),

  /**
   * VFS officer single sign-on against Microsoft Entra ID, on the Officer Login step.
   *
   * Defaults OFF, and that default is load-bearing: with it off the screen keeps its
   * narrated stub, which is what the MSW mock build and the Playwright happy path
   * drive. A fresh clone with no Entra configuration must still be able to walk the
   * whole wizard offline.
   *
   * ⚠ Note what is NOT here: a client secret. Unlike the VFS Client credentials above,
   * this is an OAuth2 *public* client using the authorization-code flow with PKCE —
   * the per-request `code_challenge` is what proves the app's identity, which is
   * precisely the mechanism that lets a browser authenticate with no secret to keep.
   * The tenant and client IDs below are public identifiers; they appear in the
   * address bar on every sign-in. So there is nothing for vite.config.ts to refuse
   * to build, and no production guard is needed.
   */
  VITE_OFFICER_SSO: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),

  VITE_ENTRA_TENANT_ID: z.string().default(''),
  VITE_ENTRA_CLIENT_ID: z.string().default(''),

  /**
   * Empty means `window.location.origin`, which is what the registered reply URL is
   * (the app root, not the officer route). Kept overridable because a deployment
   * behind a path prefix would need to say so, and because the value must match the
   * app registration character for character.
   *
   * ⚠ It must be registered under the **Single-page application** platform, not
   * **Web**. A Web reply URL rejects the PKCE token exchange with AADSTS9002326,
   * and only at the token step — the Microsoft sign-in screen itself works fine,
   * which makes it an easy failure to misread.
   */
  VITE_ENTRA_REDIRECT_URI: z.string().default(''),

  /**
   * Where Entra returns the browser once the officer's session has been ended.
   * Empty means `window.location.origin` — the app root, which redirects on to the
   * ID-number screen, and therefore the right landing spot for every exit that
   * signs the officer out.
   *
   * ⚠ Like the reply URL above, this must be registered against the app — as a
   * **logout URL** — or Entra ignores it and parks the officer on its own "You
   * signed out" page. The sign-out still happens; only the return trip is lost.
   */
  VITE_ENTRA_POST_LOGOUT_REDIRECT_URI: z.string().default(''),

  VITE_ENTRA_AUTHORITY_HOST: z.string().default('https://login.microsoftonline.com'),

  /** Space-separated, as the authorize URL carries them. */
  VITE_ENTRA_SCOPES: z.string().default('User.Read openid profile offline_access'),

  VITE_ENTRA_GRAPH_URL: z.string().default('https://graph.microsoft.com/v1.0'),
})

const parsed = schema.safeParse(import.meta.env)

if (!parsed.success) {
  const issues = parsed.error.issues.map((i) => `  ${i.path.join('.')}: ${i.message}`).join('\n')
  throw new Error(`Invalid environment configuration:\n${issues}`)
}

const data = parsed.data

if (data.VITE_FACE_MOCK_IMAGE && data.VITE_APP_ENV === 'prod') {
  throw new Error(
    'VITE_FACE_MOCK_IMAGE cannot be enabled in a production build: every citizen would ' +
      "have a hardcoded stranger's face sent for verification instead of their own. Set " +
      'VITE_FACE_MOCK_IMAGE=false — see docs/api-gaps.md #26.'
  )
}

/**
 * The build refuses each of these too (vite.config.ts). These are the second half of
 * the same pair the mock face already had: the build guard stops such an artefact
 * being produced, this one stops one that was produced anyway from running.
 */
if (data.VITE_ENABLE_MSW && data.VITE_APP_ENV === 'prod') {
  throw new Error(
    'VITE_ENABLE_MSW cannot be enabled in a production build: every request would be ' +
      'answered by the in-browser mock, so applications would appear to succeed while ' +
      'reaching no backend at all. Set VITE_ENABLE_MSW=false.'
  )
}

if (!data.VITE_OFFICER_SSO && data.VITE_APP_ENV === 'prod') {
  throw new Error(
    'VITE_OFFICER_SSO cannot be disabled in a production build: the Officer Login step ' +
      'would fall back to the mock officer, letting anyone past the step-7 verification ' +
      'and recording it against a real application. See docs/adr/0012-officer-sso-via-entra-id.md.'
  )
}

/**
 * No secret may reach the browser under any name. Vite inlines every `VITE_*` value
 * — and the key names with it — into the bundle, so a variable whose name says it is
 * a secret is a published credential. vite.config.ts refuses to build one; this is
 * the same rule at first paint, for a bundle produced some other way.
 *
 * Scoped to `VITE_` because under Vitest `import.meta.env` IS `process.env`, where a
 * developer's shell legitimately carries `AWS_SECRET_ACCESS_KEY` and friends.
 */
const secretShapedKeys = Object.keys(import.meta.env).filter((key) =>
  /^VITE_.*(SECRET|PASSWORD|PRIVATE_KEY)/i.test(key)
)
if (secretShapedKeys.length > 0) {
  throw new Error(
    `Refusing to start with a secret-shaped environment key in the bundle: ${secretShapedKeys.join(', ')}. ` +
      'The browser holds no credential — the backend issues it a session cookie instead. ' +
      'Remove the variable; see docs/adr/0016-server-issued-session-cookie.md.'
  )
}

/**
 * The backend URL is empty, `/`, or an origin — never an origin plus a route
 * namespace, and never a same-origin prefix.
 *
 * The namespace case is silent: buildVfsClientUrl concatenates, so `https://host/api/v1`
 * + `/api/v1/session` asks for `/api/v1/api/v1/session` and every VFS route 404s. The
 * CSP is derived from the origin alone (vite/csp.ts), so it comes out correct and
 * gives no warning either — the failure would first appear as a dead bootstrap.
 *
 * A same-origin prefix (`/vfs`) used to be legal, for a hypothetical proxy. It cannot
 * be now: the session cookie is scoped to `Path=/api` by the backend, so a request to
 * `/vfs/api/v1/…` would never carry it and every call would loop through a bootstrap
 * into `session_expired`. Same-origin means the routes at `/api/…`, nothing else.
 */
const backendUrl = data.VITE_VFS_BACKEND_URL.trim()
if (backendUrl.startsWith('/') && backendUrl.replace(/\/+$/, '') !== '') {
  throw new Error(
    `VITE_VFS_BACKEND_URL cannot be a same-origin prefix (${backendUrl}): the session ` +
      'cookie is scoped to /api, so routes served anywhere else never receive it. Leave it ' +
      'empty for a same-site deployment, or give the backend origin for local development.'
  )
}
if (backendUrl && !backendUrl.startsWith('/')) {
  let pathname: string
  try {
    pathname = new URL(backendUrl).pathname
  } catch {
    throw new Error(
      `VITE_VFS_BACKEND_URL is not a valid URL: ${data.VITE_VFS_BACKEND_URL}. Leave it ` +
        'empty for a same-site deployment, or give the backend origin (https://host).'
    )
  }
  if (pathname.replace(/\/+$/, '') !== '') {
    throw new Error(
      `VITE_VFS_BACKEND_URL must be a host with no path, but carries "${pathname}": ` +
        `${data.VITE_VFS_BACKEND_URL}. Every VFS route already carries its own path, so ` +
        'this would be sent twice and 404. Put the route namespace in VITE_VFS_API_PREFIX ' +
        '(UAT: /api/v1/DHASID) and leave this as the origin alone.'
    )
  }
}

/**
 * Leading slash forced, trailing slash dropped, so `api/v1/DHASID/` and
 * `/api/v1/DHASID` build the same URL. Empty stays empty — a backend serving the
 * routes at its own root.
 */
function normalisePrefix(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '')
  if (trimmed === '') return ''
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

/**
 * Both are public identifiers rather than secrets, so this is not a confidentiality
 * check — it is a "you turned SSO on and it cannot possibly work" check. MSAL would
 * otherwise construct an authority of `.../` and fail at the first redirect with a
 * message about an unknown tenant, three screens into an application.
 */
if (data.VITE_OFFICER_SSO && !(data.VITE_ENTRA_TENANT_ID && data.VITE_ENTRA_CLIENT_ID)) {
  throw new Error(
    'VITE_OFFICER_SSO is enabled but VITE_ENTRA_TENANT_ID or VITE_ENTRA_CLIENT_ID is empty: ' +
      'the officer sign-in has no app registration to authenticate against.'
  )
}

export const env = {
  apiTimeoutMs: parsed.data.VITE_API_TIMEOUT_MS,
  appEnv: parsed.data.VITE_APP_ENV,
  enableMsw: parsed.data.VITE_ENABLE_MSW,
  persistApplicantDetails: parsed.data.VITE_PERSIST_APPLICANT_DETAILS,
  faceMaxAttempts: parsed.data.VITE_FACE_MAX_ATTEMPTS,

  sessionTtlMinutes: parsed.data.VITE_SESSION_TTL_MINUTES,
  /** Temporary fixed face for §4.3 — see the schema comment above. */
  faceMockImage: parsed.data.VITE_FACE_MOCK_IMAGE,

  isProduction: parsed.data.VITE_APP_ENV === 'prod',

  /** VFS's own backend — see the schema comment above. Empty means same origin. */
  vfsClientBaseUrl: parsed.data.VITE_VFS_BACKEND_URL,

  /**
   * The route namespace, normalised once here rather than at each of the seven call
   * sites. See lib/http/vfsclient/routes.ts, and the schema comment above for why it is
   * not part of vfsClientBaseUrl.
   */
  vfsClientApiPrefix: normalisePrefix(parsed.data.VITE_VFS_API_PREFIX),

  /** Officer SSO — see the schema comments above. */
  officerSso: parsed.data.VITE_OFFICER_SSO,
  entraClientId: parsed.data.VITE_ENTRA_CLIENT_ID,
  /**
   * The single-tenant authority. Single-tenant and not `/common`: this application
   * is for VFS staff, and `/common` would let any Microsoft account reach the
   * consent screen before being refused.
   */
  entraAuthority: `${parsed.data.VITE_ENTRA_AUTHORITY_HOST.replace(/\/$/, '')}/${
    parsed.data.VITE_ENTRA_TENANT_ID
  }`,
  /** Empty means "use window.location.origin" — resolved in lib/auth/officerSso.ts. */
  entraRedirectUri: parsed.data.VITE_ENTRA_REDIRECT_URI,
  /** Same convention as the reply URL above, and resolved in the same place. */
  entraPostLogoutRedirectUri: parsed.data.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI,
  entraScopes: parsed.data.VITE_ENTRA_SCOPES.split(/\s+/).filter(Boolean),
  entraGraphUrl: parsed.data.VITE_ENTRA_GRAPH_URL.replace(/\/$/, ''),
} as const

export type Env = typeof env
